import * as React from "react";
import { Link, Outlet } from "react-router-dom";
import { EduViaOSLogo, EduViaOSMark, EduViaOSWordmark, ThemeSwitcher } from "@eduviaos/ui-kit";

/**
 * Outer chrome for every public page.
 *
 * Intentionally minimal — parents land here under stress (deadlines,
 * mobile data, screen readers), so the header carries only:
 *   1. The EduViaOS wordmark + a quiet link back to `/`
 *   2. A theme switcher (system / light / dark)
 *
 * No login link, no support email link in the header — those live in
 * the footer where they don't compete with the form for attention.
 *
 * Tenant branding is rendered by the page-level component (IntakePage,
 * TrackPage), not the frame, because the frame mounts before the
 * tenant-name fetch resolves.
 */
export function AppFrame() {
  return (
    <div className="bg-background text-foreground flex min-h-screen flex-col">
      <header className="border-border/60 bg-background/80 sticky top-0 z-30 border-b backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-3xl items-center justify-between px-4 py-3 sm:px-6">
          <Link
            to="/"
            className="text-foreground inline-flex items-center rounded-md text-base transition-opacity duration-150 ease-out hover:opacity-80"
          >
            <EduViaOSLogo size={26} />
          </Link>

          <ThemeSwitcher srLabel="Toggle colour theme" />
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 sm:py-10">
          <Outlet />
        </div>
      </main>

      <footer className="border-border/60 text-muted-foreground border-t">
        <div className="mx-auto flex w-full max-w-3xl flex-col items-start justify-between gap-2 px-4 py-4 text-xs sm:flex-row sm:items-center sm:px-6">
          <span className="inline-flex items-center gap-1.5">
            Powered by
            <EduViaOSMark className="size-4" />
            <EduViaOSWordmark className="text-foreground" />
          </span>
          <span className="opacity-80">
            Need help? Reach out to your institution directly — they own this enquiry.
          </span>
        </div>
      </footer>
    </div>
  );
}

/**
 * Reusable card frame for the page body. Used by every public page
 * (Intake, Track, NotFound) so the visual rhythm is consistent.
 */
export function PageCard({
  title,
  subtitle,
  children,
  footer,
}: {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <section className="bg-card text-card-foreground border-border/60 ring-border/40 rounded-xl border p-5 shadow-sm ring-1 sm:p-7">
      {(title || subtitle) && (
        <header className="mb-5">
          {title && (
            <h1 className="text-foreground text-xl font-semibold tracking-tight sm:text-2xl">
              {title}
            </h1>
          )}
          {subtitle && <p className="text-muted-foreground mt-1.5 text-sm">{subtitle}</p>}
        </header>
      )}
      {children}
      {footer && <footer className="border-border/60 mt-6 border-t pt-4">{footer}</footer>}
    </section>
  );
}
